/*
 * QR Adisyon API client.
 *
 * Public endpoints used by the QR menu "Hesabım / Adisyon" screen:
 *   GET  /api/qr/{uniqueUrl}/adisyon              - live bill snapshot
 *   POST /api/qr/{uniqueUrl}/customer/identify    - phone-based customer lookup
 *   POST /api/qr/{uniqueUrl}/customer/history     - paginated past orders
 *
 * No auth header is required - security relies on the QR's UUID + the
 * customer's own phone number.
 */

import axios from 'axios';
import { base_url } from './index';

// ---------- Domain types (mirror backend response shape) ----------

export interface QrAdisyonRestaurant {
  id: number;
  name: string;
  logo: string | null;
  currency: string;
}

export interface QrAdisyonTable {
  id: number;
  name: string;
  unique_url: string;
}

export interface QrAdisyonStockDetail {
  id: number;
  price: number;
  unit?: string;
  count?: number | string;
}

export interface QrAdisyonStockLine {
  pivot_id: number;
  stock_id: number;
  name: string;
  image: string | null;
  quantity: number;
  unit_price: number;
  total_price: number;
  kitchen_status: 'pending' | 'preparing' | 'ready' | string;
  is_printed: boolean;
  detail: QrAdisyonStockDetail | null;
}

export interface QrAdisyonStockSetLine {
  pivot_id: number;
  set_id: number;
  name: string;
  image: string | null;
  quantity: number;
  unit_price: number;
  total_price: number;
  kitchen_status: 'pending' | 'preparing' | 'ready' | string;
  is_printed: boolean;
}

export interface QrAdisyonTimeCharge {
  id: number;
  title: string;
  amount: number;
}

export interface QrAdisyonDiscount {
  id: number;
  amount: number;
  type: string;
  reason: string | null;
}

export interface QrAdisyonPrepayment {
  id: number;
  amount: number;
  type: string;
  date: string;
}

export interface QrAdisyonPayment {
  id: number;
  amount: number;
  type: string;
  date: string;
}

export interface QrAdisyonOrderTotals {
  items_total: number;
  time_charges_total: number;
  subtotal: number;
  discount_total: number;
  net_total: number;
  prepayment_total: number;
  payment_total: number;
  remaining: number;
}

export interface QrAdisyonGrandTotals extends QrAdisyonOrderTotals {
  currency: string;
}

export interface QrAdisyonOrder {
  order_id: number;
  order_number: string;
  status: 'approved' | string;
  table_name: string;
  created_at: string;
  stocks: QrAdisyonStockLine[];
  stock_sets: QrAdisyonStockSetLine[];
  time_charges: QrAdisyonTimeCharge[];
  discounts: QrAdisyonDiscount[];
  prepayments: QrAdisyonPrepayment[];
  payments: QrAdisyonPayment[];
  totals: QrAdisyonOrderTotals;
}

export interface QrLoyaltyPoints {
  balance: number;
  total_earned: number;
  total_spent: number;
  value_in_currency: number;
}

export interface QrAdisyonCustomer {
  id: number;
  name: string;
  phone: string;
  balance: number;
  loyalty_points: QrLoyaltyPoints | null;
}

export interface QrAdisyonResponse {
  table: QrAdisyonTable;
  restaurant: QrAdisyonRestaurant;
  orders: QrAdisyonOrder[];
  totals: QrAdisyonGrandTotals;
  customer: QrAdisyonCustomer | null;
}

export interface QrCustomerLifetime {
  total_orders: number;
  total_spent: number;
  first_visit: string | null;
  last_visit: string | null;
}

export interface QrIdentifyResponse {
  found: boolean;
  customer: QrAdisyonCustomer | null;
  lifetime: QrCustomerLifetime;
}

export interface QrHistoryOrder {
  order_id: number;
  order_number: string;
  date: string;
  table_name: string;
  items_count: number;
  items_summary: string;
  total: number;
  paid_amount: number;
  payment_methods: Array<'cash' | 'bank' | 'customer_balance' | string>;
}

export interface QrHistoryPagination {
  page: number;
  per_page: number;
  total: number;
  last_page: number;
}

export interface QrHistoryResponse {
  customer: Pick<QrAdisyonCustomer, 'id' | 'name' | 'phone'>;
  orders: QrHistoryOrder[];
  pagination: QrHistoryPagination;
}

// ---------- HTTP client ----------

// A dedicated axios instance keeps these calls public (no auth header) and
// gives us a single place to tune timeouts/retries later.
const qrClient = axios.create({
  baseURL: base_url,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
  timeout: 15000,
});

const buildAdisyonUrl = (uniqueUrl: string, phone?: string | null) => {
  const base = `/qr/${encodeURIComponent(uniqueUrl)}/adisyon`;
  if (!phone) return base;
  return `${base}?phone=${encodeURIComponent(phone)}`;
};

/** Fetch the live bill (adisyon) for a QR-coded table. */
export async function getQrAdisyon(
  uniqueUrl: string,
  phone?: string | null,
  signal?: AbortSignal,
): Promise<QrAdisyonResponse> {
  const res = await qrClient.get<QrAdisyonResponse>(
    buildAdisyonUrl(uniqueUrl, phone),
    { signal },
  );
  return res.data;
}

/** Identify a customer by phone for this restaurant's QR session. */
export async function identifyQrCustomer(
  uniqueUrl: string,
  phone: string,
  signal?: AbortSignal,
): Promise<QrIdentifyResponse> {
  const res = await qrClient.post<QrIdentifyResponse>(
    `/qr/${encodeURIComponent(uniqueUrl)}/customer/identify`,
    { phone },
    { signal },
  );
  return res.data;
}

/** Paginated past-order history for the identified customer. */
export async function getQrCustomerHistory(
  uniqueUrl: string,
  phone: string,
  page = 1,
  perPage = 10,
  signal?: AbortSignal,
): Promise<QrHistoryResponse> {
  const safePerPage = Math.min(Math.max(perPage, 1), 50);
  const safePage = Math.max(page, 1);
  const res = await qrClient.post<QrHistoryResponse>(
    `/qr/${encodeURIComponent(uniqueUrl)}/customer/history`,
    { phone, page: safePage, per_page: safePerPage },
    { signal },
  );
  return res.data;
}

// ---------- localStorage helpers ----------
// The customer phone is intentionally low-trust (no real auth) so we mirror
// what the markdown spec recommends: persist per QR uniqueUrl so refreshing
// the page restores their session without forcing them to retype.

export const qrPhoneStorageKey = (uniqueUrl: string) =>
  `qr_customer_phone:${uniqueUrl}`;

export function loadStoredQrPhone(uniqueUrl: string): string | null {
  try {
    return localStorage.getItem(qrPhoneStorageKey(uniqueUrl));
  } catch {
    return null;
  }
}

export function saveQrPhone(uniqueUrl: string, phone: string): void {
  try {
    localStorage.setItem(qrPhoneStorageKey(uniqueUrl), phone);
  } catch {
    /* private mode / quota — ignore */
  }
}

export function clearQrPhone(uniqueUrl: string): void {
  try {
    localStorage.removeItem(qrPhoneStorageKey(uniqueUrl));
  } catch {
    /* ignore */
  }
}
